import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import Navigation from '../components/common/Navigation';
import Footer from '../components/common/Footer';

interface LegalPageData {
  _id: string;
  type: string;
  title: string;
  content: string;
  summary?: string;
  sections?: Array<{
    title: string;
    content: string;
  }>;
  version: string;
  effectiveDate: Date;
  lastReviewDate?: Date;
}

const LegalPage: React.FC = () => {
  const { type } = useParams<{ type: string }>();
  const [page, setPage] = useState<LegalPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (type) {
      fetchLegalPage();
    }
  }, [type]);

  const fetchLegalPage = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/legal/pages/${type}`);
      setPage(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching legal page:', err);
      setError('Page not found');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Page Not Found</h1>
          <p className="text-gray-600">The requested legal page could not be found.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-3xl font-bold text-gray-900">{page.title}</h1>
            <div className="mt-2 flex items-center gap-4 text-sm text-gray-600">
              <span>Version {page.version}</span>
              <span>•</span>
              <span>Effective Date: {new Date(page.effectiveDate).toLocaleDateString()}</span>
              {page.lastReviewDate && (
                <>
                  <span>•</span>
                  <span>Last Reviewed: {new Date(page.lastReviewDate).toLocaleDateString()}</span>
                </>
              )}
            </div>
          </motion.div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow-md p-8"
        >
          {page.summary && (
            <div className="mb-8 p-4 bg-blue-50 rounded-lg">
              <h2 className="text-lg font-semibold text-blue-900 mb-2">Summary</h2>
              <p className="text-blue-800">{page.summary}</p>
            </div>
          )}

          {page.sections && page.sections.length > 0 ? (
            <div className="space-y-8">
              {page.sections.map((section, index) => (
                <motion.section
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * (index + 1) }}
                >
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">
                    {section.title}
                  </h2>
                  <div 
                    className="prose prose-gray max-w-none"
                    dangerouslySetInnerHTML={{ __html: section.content }}
                  />
                </motion.section>
              ))}
            </div>
          ) : (
            <div 
              className="prose prose-gray max-w-none"
              dangerouslySetInnerHTML={{ __html: page.content }}
            />
          )}
        </motion.div>

        {/* Contact Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-12 p-6 bg-gray-100 rounded-lg"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Questions about this policy?
          </h3>
          <p className="text-gray-700">
            If you have any questions about this {page.title.toLowerCase()}, please contact us at{' '}
            <a href="mailto:legal@twentyfourcapital.com" className="text-primary-600 hover:text-primary-700">
              legal@twentyfourcapital.com
            </a>
          </p>
        </motion.div>
      </main>
    </div>
    <Footer />
    </>
  );
};

export default LegalPage; 